import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoupleSpace() {
  const { authHeader } = useAuth();
  const [kinds, setKinds] = useState([]);
  const [activeKind, setActiveKind] = useState('memories');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [editing, setEditing] = useState(null); // entry being edited or null
  const [draft, setDraft] = useState({ title: '', content: '', mood: '' });

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API}/api/couple/meta`);
        setKinds(m.data.kinds || []);
      } catch { toast.error('Could not load couple space.'); }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/couple/entries?kind=${activeKind}`, { headers: authHeader() })
      .then(r => setEntries(r.data.entries || []))
      .catch(() => toast.error('Could not load entries.'))
      .finally(() => setLoading(false));
  }, [activeKind]);

  const activeKindMeta = kinds.find(k => k.id === activeKind) || {};

  const openCompose = () => {
    setEditing(null);
    setDraft({ title: '', content: '', mood: '' });
    setComposeOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setDraft({ title: entry.title || '', content: entry.content, mood: entry.mood || '' });
    setComposeOpen(true);
  };

  const closeCompose = () => { setComposeOpen(false); setEditing(null); };

  const save = async () => {
    if (!draft.content.trim()) { toast.error('Add something to remember.'); return; }
    try {
      if (editing) {
        const r = await axios.patch(`${API}/api/couple/entries/${editing.id}`, draft, { headers: authHeader() });
        setEntries(prev => prev.map(e => e.id === editing.id ? r.data.entry : e));
        toast.success('Saved.');
      } else {
        const r = await axios.post(`${API}/api/couple/entries`, { ...draft, kind: activeKind }, { headers: authHeader() });
        setEntries(prev => [r.data.entry, ...prev]);
        toast.success('Saved to your private space.');
      }
      closeCompose();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save.';
      toast.error(typeof msg === 'string' ? msg : 'Could not save.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/couple/entries/${id}`, { headers: authHeader() });
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Deleted.');
    } catch { toast.error('Could not delete.'); }
  };

  return (
    <div className="couple-shell" style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }} data-testid="couple-space">
      <div className="education-aurora" aria-hidden="true" />

      <div style={{ padding: '18px 16px 6px', position: 'relative', zIndex: 1 }}>
        <div className="eyebrow">A Private Space</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.25rem', marginBottom: 4 }}>For <span className="gold-italic">Two</span></div>
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.98rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.6 }}>
          A secure vault for memories · gratitude · date ideas · goals · fantasies · check-ins. Only you can see it.
        </p>
      </div>

      {/* Kind chips — 6 visible in a 3-column grid */}
      <div style={{ padding: '0 16px 12px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, position: 'relative', zIndex: 1 }} data-testid="couple-kind-chips">
        {kinds.map(k => (
          <button
            key={k.id}
            onClick={() => setActiveKind(k.id)}
            data-testid={`couple-kind-${k.id}`}
            style={{
              padding: '10px 8px',
              fontSize: '0.73rem',
              letterSpacing: '0.06em',
              fontFamily: 'var(--font-ui)',
              background: activeKind === k.id ? `${k.color}28` : 'var(--bg2)',
              color: activeKind === k.id ? k.color : 'var(--text-muted)',
              border: `1px solid ${activeKind === k.id ? k.color : 'var(--border-soft)'}`,
              cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
              minHeight: 58,
              textAlign: 'center',
              lineHeight: 1.15,
              transition: 'all 0.18s ease',
            }}
          >
            <i className={`fa-solid ${k.icon}`} style={{ fontSize: '0.98rem' }} />
            {k.label}
          </button>
        ))}
      </div>

      {/* Compose button */}
      <div style={{ padding: '0 16px 14px', position: 'relative', zIndex: 1 }}>
        <button
          onClick={openCompose}
          className="btn-gold w-full"
          style={{ fontSize: '0.81rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          data-testid="couple-add-btn"
        >
          <i className="fa-solid fa-plus" /> ADD TO {(activeKindMeta.label || '').toUpperCase()}
        </button>
      </div>

      {/* Entries */}
      <div style={{ padding: '0 16px 0', position: 'relative', zIndex: 1 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="gold-loader" /></div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '40px 16px', textAlign: 'center', border: '1px dashed var(--border-soft)', background: 'var(--bg2)' }}>
            <i className={`fa-solid ${activeKindMeta.icon || 'fa-feather'}`} style={{ fontSize: '1.9rem', color: activeKindMeta.color || 'var(--gold)', marginBottom: 10, display: 'block' }} />
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '1.18rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              {activeKindMeta.prompt || 'Begin a quiet entry…'}
            </p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {entries.map(e => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
                style={{
                  padding: '14px 16px',
                  marginBottom: 8,
                  background: 'var(--bg2)',
                  border: `1px solid var(--border-soft)`,
                  borderLeft: `3px solid ${activeKindMeta.color || 'var(--gold)'}`,
                }}
                data-testid={`couple-entry-${e.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {e.title && <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.06rem', marginBottom: 2 }}>{e.title}</div>}
                    <div style={{ fontSize: '0.68rem', letterSpacing: '0.12em', color: 'var(--text-dim)', fontWeight: 700 }}>
                      {new Date(e.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {e.mood && <span style={{ marginLeft: 8, color: activeKindMeta.color || 'var(--gold)' }}>· {e.mood}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(e)} title="Edit" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer' }} data-testid={`couple-edit-${e.id}`}>
                      <i className="fa-solid fa-pen" style={{ fontSize: '0.75rem' }} />
                    </button>
                    <button onClick={() => remove(e.id)} title="Delete" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer' }} data-testid={`couple-delete-${e.id}`}>
                      <i className="fa-solid fa-trash" style={{ fontSize: '0.75rem' }} />
                    </button>
                  </div>
                </div>
                <p style={{ fontSize: '1.06rem', color: 'var(--text)', lineHeight: 1.65, whiteSpace: 'pre-wrap', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}>
                  {e.content}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Compose modal */}
      <AnimatePresence>
        {composeOpen && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeCompose}>
            <motion.div
              className="modal-sheet"
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              onClick={ev => ev.stopPropagation()}
              style={{ padding: 22, maxWidth: 460, width: '92%' }}
              data-testid="couple-compose"
            >
              <div className="eyebrow" style={{ color: activeKindMeta.color || 'var(--gold)' }}>
                {editing ? 'Edit' : 'New entry'} · {activeKindMeta.label}
              </div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', color: 'var(--gold)', fontSize: '0.98rem', marginBottom: 14 }}>
                {activeKindMeta.prompt}
              </div>

              <input
                type="text"
                value={draft.title}
                onChange={e => setDraft({ ...draft, title: e.target.value })}
                placeholder="Title (optional)"
                data-testid="couple-input-title"
                style={{
                  width: '100%', padding: '10px 12px', marginBottom: 10,
                  background: 'var(--bg)', border: '1px solid var(--border-soft)',
                  color: 'var(--text)', fontSize: '0.93rem', fontFamily: 'var(--font-ui)',
                }}
              />
              <textarea
                value={draft.content}
                onChange={e => setDraft({ ...draft, content: e.target.value })}
                placeholder="Write freely…"
                rows={6}
                data-testid="couple-input-content"
                style={{
                  width: '100%', padding: '12px', marginBottom: 10,
                  background: 'var(--bg)', border: '1px solid var(--border-soft)',
                  color: 'var(--text)', fontSize: '0.96rem', fontFamily: 'Cormorant Garamond, serif',
                  fontStyle: 'italic', lineHeight: 1.55, resize: 'vertical',
                }}
              />
              <input
                type="text"
                value={draft.mood}
                onChange={e => setDraft({ ...draft, mood: e.target.value })}
                placeholder="Mood / tag (optional)"
                data-testid="couple-input-mood"
                style={{
                  width: '100%', padding: '10px 12px', marginBottom: 16,
                  background: 'var(--bg)', border: '1px solid var(--border-soft)',
                  color: 'var(--text)', fontSize: '0.9rem', fontFamily: 'var(--font-ui)',
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={closeCompose} className="btn-outline" style={{ flex: 1, padding: '12px', fontSize: '0.79rem' }} data-testid="couple-cancel">
                  CANCEL
                </button>
                <button onClick={save} className="btn-gold" style={{ flex: 1, padding: '12px', fontSize: '0.79rem' }} data-testid="couple-save">
                  SAVE
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
